import React, { ReactElement, useMemo } from 'react'
import { Box, Typography, Paper, Alert, Chip } from '@mui/material'
import {
  Lightbulb as ReasoningIcon,
  FormatListBulleted as StepsIcon,
  Info as InfoIcon,
  HelpOutlined as QuestionIcon,
} from '@mui/icons-material'
import JsonView from '@uiw/react-json-view'
import type { MotherSessionDetail, PlanGenerationResponse } from '../types'

type PlanTabProps = {
  sessionDetail: MotherSessionDetail
}

export default ({ sessionDetail }: PlanTabProps): ReactElement => {
  let { plan } = sessionDetail

  let parsedPlan = useMemo<PlanGenerationResponse | null>(() => {
    if (!plan) return null
    try {
      return JSON.parse(plan)
    } catch {
      return null
    }
  }, [plan])

  if (!plan) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Plan data is not available for this session. This field is only
          populated for sessions created after the plan endpoint was deployed.
        </Alert>
      </Box>
    )
  }

  if (!parsedPlan) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to parse plan data. The data may be corrupted.
        </Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* LLM Reasoning */}
      <Paper sx={{ p: 3, bgcolor: 'background.paperDark' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <ReasoningIcon color="primary" />
          <Typography variant="h6">LLM Decision Reasoning</Typography>
        </Box>
        <Box
          sx={{
            bgcolor: 'background.paper',
            p: 2,
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
            whiteSpace: 'pre-wrap',
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            lineHeight: 1.6,
          }}
        >
          {parsedPlan.reasoning || 'No reasoning provided'}
        </Box>
      </Paper>

      {/* Response Format */}
      {parsedPlan.responseFormat && (
        <Paper sx={{ p: 3, bgcolor: 'background.paperDark' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <InfoIcon color="info" />
            <Typography variant="h6">Response Format Requirements</Typography>
          </Box>
          <Box
            sx={{
              bgcolor: 'background.paper',
              p: 2,
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              fontSize: '0.875rem',
            }}
          >
            {parsedPlan.responseFormat}
          </Box>
        </Paper>
      )}

      {/* Missing Information / Ambiguities */}
      {parsedPlan.missingInformation &&
        parsedPlan.missingInformation.length > 0 && (
          <Paper sx={{ p: 3, bgcolor: 'background.paperDark' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <QuestionIcon color="warning" />
              <Typography variant="h6">
                Missing Information / Ambiguities
              </Typography>
            </Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              The LLM identified {parsedPlan.missingInformation.length} area(s)
              where the objective was ambiguous or required clarification:
            </Alert>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {parsedPlan.missingInformation.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    bgcolor: 'background.paper',
                    p: 2,
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'warning.main',
                    borderLeft: 4,
                  }}
                >
                  <Typography variant="body2">{item}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        )}

      {/* Workflow Steps */}
      {parsedPlan.steps && parsedPlan.steps.length > 0 && (
        <Paper sx={{ p: 3, bgcolor: 'background.paperDark' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <StepsIcon color="success" />
            <Typography variant="h6">Planned Workflow Steps</Typography>
            <Chip
              label={`${parsedPlan.steps.length} step${parsedPlan.steps.length !== 1 ? 's' : ''}`}
              size="small"
              color="success"
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These are the original workflow steps as planned by the LLM before
            execution.
          </Typography>
          <Box
            sx={{
              bgcolor: 'background.paper',
              p: 2,
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
              maxHeight: 600,
              overflowY: 'auto',
            }}
          >
            <JsonView
              value={parsedPlan.steps}
              style={
                {
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  fontSize: '14px',
                  '--w-rjv-color': '#ffffff',
                  '--w-rjv-key-string': '#9db7ff',
                  '--w-rjv-background-color': 'transparent',
                } as any
              }
              displayDataTypes={false}
              displayObjectSize={true}
              enableClipboard={true}
              collapsed={2}
            />
          </Box>
        </Paper>
      )}

      {/* Context */}
      {parsedPlan.context && Object.keys(parsedPlan.context).length > 0 && (
        <Paper sx={{ p: 3, bgcolor: 'background.paperDark' }}>
          <Typography variant="h6" gutterBottom>
            Additional Context
          </Typography>
          <Box
            sx={{
              bgcolor: 'background.paper',
              p: 2,
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
            }}
          >
            <JsonView
              value={parsedPlan.context}
              style={
                {
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  fontSize: '14px',
                  '--w-rjv-color': '#ffffff',
                  '--w-rjv-key-string': '#9db7ff',
                  '--w-rjv-background-color': 'transparent',
                } as any
              }
              displayDataTypes={false}
              displayObjectSize={false}
              enableClipboard={true}
            />
          </Box>
        </Paper>
      )}
    </Box>
  )
}
